interface EventMap {
  updated: any;
  [key: string]: any[];
}
export interface PubSubMethods {
  on<K extends keyof EventMap>(
    event: K,
    callback: (...args: EventMap[K]) => void,
  ): void;
  once<K extends keyof EventMap>(
    event: K,
    callback: (...args: EventMap[K]) => void,
  ): void;
  off<K extends keyof EventMap>(
    event: K,
    callback?: (...args: EventMap[K]) => void,
  ): void;
  emit<K extends keyof EventMap>(event: K, ...args: EventMap[K]): boolean;
  listenerCount<K extends keyof EventMap>(event: K): number;
  eventNames(): string[];
  removeAllListeners<K extends keyof EventMap>(event?: K): void;
}

export class PubSub implements PubSubMethods {
  private _eventListeners: Map<string, Function[]> = new Map();

  // 订阅事件
  on<K extends keyof EventMap>(
    event: K,
    callback: (...args: EventMap[K]) => void,
  ): void {
    if (!this._eventListeners.has(event as string)) {
      this._eventListeners.set(event as string, []);
    }
    this._eventListeners.get(event as string)!.push(callback);
  }

  // 订阅一次性事件
  once<K extends keyof EventMap>(
    event: K,
    callback: (...args: EventMap[K]) => void,
  ): void {
    const onceWrapper = (...args: EventMap[K]) => {
      callback(...args);
      this.off(event, onceWrapper);
    };
    this.on(event, onceWrapper);
  }

  // 取消订阅
  off<K extends keyof EventMap>(
    event: K,
    callback?: (...args: EventMap[K]) => void,
  ): void {
    if (!callback) {
      // 如果没有提供回调函数，移除所有监听器
      this._eventListeners.delete(event as string);
    } else {
      const callbacks = this._eventListeners.get(event as string);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
        // 如果没有监听器了，删除这个事件
        if (callbacks.length === 0) {
          this._eventListeners.delete(event as string);
        }
      }
    }
  }

  // 发布事件
  emit<K extends keyof EventMap>(event: K, ...args: EventMap[K]): boolean {
    console.log('emit', event);
    const callbacks = this._eventListeners.get(event as string);
    if (!callbacks || callbacks.length === 0) {
      return false;
    }

    callbacks.forEach(callback => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in event listener for '${String(event)}':`, error);
      }
    });

    return true;
  }

  // 获取事件监听器数量
  listenerCount<K extends keyof EventMap>(event: K): number {
    const callbacks = this._eventListeners.get(event as string);
    return callbacks ? callbacks.length : 0;
  }

  // 获取所有事件名称
  eventNames(): string[] {
    return Array.from(this._eventListeners.keys());
  }

  // 移除所有监听器
  removeAllListeners<K extends keyof EventMap>(event?: K): void {
    if (event) {
      this._eventListeners.delete(event as string);
    } else {
      this._eventListeners.clear();
    }
  }
}
